import { makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { Status } from 'types/emailService';
import { UserType } from 'types/general';
import { BiGroup } from 'types/groupService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { removeAdditionalInfo, saveAdditionalInfo } from 'api/operations/groupService';
import { sendRenewalEmails } from 'api/operations/enrollmentService';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { RenewalEmail, SentEmail } from '../emailDelegate';
import { GroupEnrollmentActionContext } from '../renewalGroupActionDelegate';

interface ManageEmailsContact {
  email: string;
  firstName: string;
  lastName: string;
}

interface ManageEmailsRow {
  emailAddress: string;
  userType: UserType;
  status: Status;
  userName: string;
  isAdditional: boolean;
}

interface NewContactFormData {
  email: string;
  firstName: string;
  lastName: string;
  userType: UserType;
}

interface NewContactFormFields extends DefaultFormFields {
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  userType: FormControl<UserType>;
}

const USER_TYPE_SORT_ORDER: Record<UserType, number> = {
  [UserType.AOR]: 0,
  [UserType.ADMIN]: 1,
};

function getContactName(contact: ManageEmailsContact): string {
  return `${contact.firstName} ${contact.lastName}`.trim();
}

function getUserNameByEmail(
  email: string,
  userType: UserType,
  aorContacts: ManageEmailsContact[],
  adminContacts: ManageEmailsContact[],
): string {
  const contacts = userType === UserType.AOR ? aorContacts : adminContacts;
  const matchedContact = contacts.find((contact) => contact.email === email);
  return matchedContact ? getContactName(matchedContact) : '';
}

function toContacts(sources: Array<{ email?: string; firstName?: string; lastName?: string }>): ManageEmailsContact[] {
  return sources.flatMap(({ email, firstName, lastName }) =>
    email ? [{ email, firstName: firstName ?? '', lastName: lastName ?? '' }] : [],
  );
}

function toAorContacts({ agentOfRecord, additionalAORs }: BiGroup): ManageEmailsContact[] {
  return toContacts([agentOfRecord, ...additionalAORs]);
}

function toAdminContacts({ contactInfo, additionalContactInfos }: BiGroup): ManageEmailsContact[] {
  return toContacts(
    [contactInfo, ...additionalContactInfos].map(({ primaryEmail, firstName, lastName }) => ({
      email: primaryEmail,
      firstName,
      lastName,
    })),
  );
}

export const userTypeLabels: Record<UserType, string> = {
  [UserType.AOR]: 'Broker',
  [UserType.ADMIN]: 'Admin',
};

export const newContactTypeOptions: OptionType[] = [
  {
    label: userTypeLabels[UserType.AOR],
    value: UserType.AOR,
  },
  {
    label: userTypeLabels[UserType.ADMIN],
    value: UserType.ADMIN,
  },
];

interface ManageEmailsModalDelegateParams {
  onGroupUpdated: (updatedGroup: BiGroup) => void;
}

export class ManageEmailsModalDelegate {
  private readonly _modalDelegate = new ModalDelegate();
  private readonly _onGroupUpdated: (updatedGroup: BiGroup) => void;
  private _onMarkEmailsAsSent: ((sentEmails: SentEmail[]) => void) | null = null;
  private _activeGroupContext: GroupEnrollmentActionContext | null = null;
  private _emails: ManageEmailsRow[] = [];

  public newContactForm = new FormController<NewContactFormFields>({
    email: new FormControl('', { optional: false, validations: ['isEmailValid'] }),
    firstName: new FormControl('', { optional: false, validations: ['printableCharacters'] }),
    lastName: new FormControl('', { optional: false, validations: ['printableCharacters'] }),
    userType: new FormControl(UserType.AOR),
  });

  public get isVisible(): boolean {
    return this._modalDelegate.isVisible;
  }

  public get isLoading(): boolean {
    return this._modalDelegate.isLoading;
  }

  public get subtitle(): string {
    if (!this._activeGroupContext?.group.name || !this._activeGroupContext?.enrollmentDelegate.period.effectiveDate) {
      return '';
    }

    return `${this._activeGroupContext.group.name}, ${normDateToShortAmericanDate(
      this._activeGroupContext.enrollmentDelegate.period.effectiveDate,
    )}`;
  }

  public get emails(): ManageEmailsRow[] {
    return [...this._emails].sort((a, b) => USER_TYPE_SORT_ORDER[a.userType] - USER_TYPE_SORT_ORDER[b.userType]);
  }

  public get isEmailAlreadyListed(): boolean {
    const email = this.newContactForm.fields.email.value;
    if (!email) {
      return false;
    }

    return this._emails.some((listedEmail) => listedEmail.emailAddress.toLowerCase() === email.toLowerCase());
  }

  private _setupFromEnrollmentGroupContext(context: GroupEnrollmentActionContext, emails: RenewalEmail[]): void {
    this._activeGroupContext = context;
    const aorContacts = toAorContacts(context.group);
    const adminContacts = toAdminContacts(context.group);

    this._emails = emails.map((email) => ({
      emailAddress: email.emailAddress,
      userType: email.userType,
      status: email.status,
      userName: getUserNameByEmail(email.emailAddress, email.userType, aorContacts, adminContacts),
      isAdditional: email.isAdditional,
    }));
  }

  public open(
    context: GroupEnrollmentActionContext,
    emails: RenewalEmail[],
    markEmailsAsSent: (sentEmails: SentEmail[]) => void,
  ): void {
    this._setupFromEnrollmentGroupContext(context, emails);
    this._onMarkEmailsAsSent = markEmailsAsSent;
    this._modalDelegate.open();
  }

  public close(): void {
    this._reset();
    this._modalDelegate.close();
  }

  public async saveNewContact(): Promise<void> {
    if (this.isEmailAlreadyListed) {
      return;
    }

    this._modalDelegate.setIsLoading(true);
    const newContact = this.newContactForm.getFormDataObject() as unknown as NewContactFormData;
    const updatedGroup = await saveAdditionalInfo(this._activeGroupContext?.group.id ?? '', {
      email: newContact.email,
      firstName: newContact.firstName,
      lastName: newContact.lastName,
      type: newContact.userType,
    });
    runInAction(() => {
      this._onGroupUpdated(updatedGroup);
      this._emails = [
        ...this._emails,
        {
          emailAddress: newContact.email,
          userType: newContact.userType,
          status: Status.NEW,
          userName: getContactName(newContact),
          isAdditional: true,
        },
      ];
      this.newContactForm.resetForm();
      this._modalDelegate.setIsLoading(false);
    });
  }

  public async removeContact(email: string, userType: UserType): Promise<void> {
    const contact = this._emails.find(
      (listedEmail) => listedEmail.emailAddress === email && listedEmail.userType === userType,
    );
    if (!contact?.isAdditional) {
      return;
    }

    this._modalDelegate.setIsLoading(true);
    const updatedGroup = await removeAdditionalInfo(this._activeGroupContext?.group.id ?? '', userType, email);
    runInAction(() => {
      this._onGroupUpdated(updatedGroup);
      this._emails = this._emails.filter(
        (listedEmail) => !(listedEmail.emailAddress === email && listedEmail.userType === userType),
      );
      this._modalDelegate.setIsLoading(false);
    });
  }

  public async sendEmailToUser(emailAddress: string, userType: UserType): Promise<void> {
    this._modalDelegate.setIsLoading(true);

    await sendRenewalEmails(this._activeGroupContext!.enrollmentDelegate.period.id, [
      { email: emailAddress, userType },
    ]);

    runInAction(() => {
      this._markEmailsAsSent([{ emailAddress, userType }]);
      this._modalDelegate.setIsLoading(false);
      toastManager.addToast('Email sent successfully!', ToastColorTypes.SUCCESS);
    });
  }

  public async notifyAllUsers(): Promise<void> {
    this._modalDelegate.setIsLoading(true);

    const emails = this._emails.map((email) => ({ email: email.emailAddress, userType: email.userType }));

    await sendRenewalEmails(this._activeGroupContext!.enrollmentDelegate.period.id, emails);

    runInAction(() => {
      this._markEmailsAsSent(emails.map((email) => ({ emailAddress: email.email, userType: email.userType })));
      this._modalDelegate.setIsLoading(false);
      toastManager.addToast('Emails sent successfully!', ToastColorTypes.SUCCESS);
    });
  }

  private _markEmailsAsSent(sentEmails: SentEmail[]): void {
    if (!this._onMarkEmailsAsSent) {
      throw new Error('Tried to mark emails as sent without a callback!');
    }

    this._emails = this._emails.map((email) =>
      sentEmails.some(
        (sentEmail) => sentEmail.emailAddress === email.emailAddress && sentEmail.userType === email.userType,
      )
        ? { ...email, status: Status.SENT }
        : email,
    );
    this._onMarkEmailsAsSent(sentEmails);
  }

  private _reset(): void {
    this._onMarkEmailsAsSent = null;
    this._activeGroupContext = null;
    this._emails = [];
    this.newContactForm.resetForm();
    this._modalDelegate.setIsLoading(false);
  }

  constructor({ onGroupUpdated }: ManageEmailsModalDelegateParams) {
    this._onGroupUpdated = onGroupUpdated;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
